import { Check, ExternalLink, X } from 'lucide-react';
import { CopyButton } from '@/components/ui/copy-button';
import { formatDate, formatEuros } from '@/utils/format';
import { shortUuid } from '@/utils/string';

interface DateCellProps {
  value: number | string | Date | null | undefined;
  /** Override formatter — e.g. brokers' minute-precision formatBrokerDateTime. Defaults to utils/format.formatDate. */
  format?(value: number | string | Date | null | undefined): string;
}

export const DateCell = ({ value, format }: DateCellProps) => <span className="whitespace-nowrap tabular-nums">{(format ?? formatDate)(value)}</span>;
DateCell.displayName = 'DateCell';

export const BoolCell = ({ value }: { value: boolean | null | undefined }) =>
  value ? <Check aria-label="yes" className="size-4 text-emerald-500" /> : <X aria-label="no" className="size-4 text-red-500" />;
BoolCell.displayName = 'BoolCell';

export const EuroCell = ({ value }: { value: number | null | undefined }) => (
  <span className="whitespace-nowrap tabular-nums">{value === null || value === undefined ? '—' : formatEuros(value)}</span>
);
EuroCell.displayName = 'EuroCell';

export const UuidCell = ({ value }: { value: string | null | undefined }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" title={value}>
      {shortUuid(value)}
      <CopyButton label={`Copy ${value}`} value={value} />
    </span>
  );
};
UuidCell.displayName = 'UuidCell';

/** Account holder PID: tooltip + copy + click-through to the user page in a new tab */
export const AccountHolderCell = ({ value }: { value: string | null | undefined }) => {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" title={value}>
      <a
        className="inline-flex items-center gap-1 underline underline-offset-2"
        href={`/operations/users/${value}`}
        onClick={(event) => event.stopPropagation()}
        rel="noreferrer"
        target="_blank"
      >
        {shortUuid(value)}
        <ExternalLink className="size-3" />
      </a>
      <CopyButton label={`Copy ${value}`} value={value} />
    </span>
  );
};
AccountHolderCell.displayName = 'AccountHolderCell';
